var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { PolicyValidatorErrors } from '.';
import { FinalizeSorisReturnAPI, RefundEstimationAPI, SSReturnCreateAPI, ValidateAddressAPI, ValidateZipCodeAPI, } from '../api/returns';
import { TagApiError, TagFinalizeReturnApiError } from './analytics-tagging';
/**
 * Safely checks the AxiosError for the Generic Error Model and returns the error data
 * @param error - The Axios error object.
 * @returns Returns the object inside the error (code, description and retryable) If none found, will return undefined
 */
export var getAPIErrorData = function (error) {
    var _a, _b, _c;
    var errorData = ((_c = (_b = (_a = error === null || error === void 0 ? void 0 : error.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.errors) === null || _c === void 0 ? void 0 : _c[0]) || undefined;
    return errorData;
};
export var unknownError = function () { return ({
    code: 'UNKNOWN',
    description: 'Unknown Error',
    retryable: true,
}); };
/**
 * Checks if the given error is related to a multi-shipment address error.
 * This error should only occur during phase 1 of multi vendor address
 * @param error - The Axios error object.
 * @returns True if the error is a multi-shipment address error, false otherwise.
 */
export var isMultiShipmentAddressError = function (error) {
    var _a, _b;
    var errorCode = (_a = getAPIErrorData(error)) === null || _a === void 0 ? void 0 : _a.code;
    return ((_b = error === null || error === void 0 ? void 0 : error.response) === null || _b === void 0 ? void 0 : _b.status) === 400 && errorCode === 'MULTI_SHIPMENTS';
};
export var isProtectionPlanError = function (error) {
    var _a, _b, _c, _d;
    return (error === null || error === void 0 ? void 0 : error.status) === 400 &&
        ((_d = (_c = (_b = (_a = error === null || error === void 0 ? void 0 : error.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.errors) === null || _c === void 0 ? void 0 : _c[0]) === null || _d === void 0 ? void 0 : _d.code) === 'SSR_HRTVPOLICY_ERR_4102';
};
/**
 * Gets the PolicyValidator error type based on the API response.
 *
 * @param {AxiosError} error - The AxiosError object representing the HTTP error.
 * @returns {PolicyValidatorErrors} The type of error scenario that occurred.
 *
 * @description
 * This function checks the error message of Axios (created when we throw an error inside a Promise), while also checking
 * the error status code+body of the response to determine the appropriate scenario type.
 * If the error is a 5xx network error, it returns PolicyValidatorErrors.NETWORK.
 * If the error is related to multiple shipments (e.g., status 400 and specific error code),
 *  it returns PolicyValidatorErrors.MULTI_SHIPMENTS.
 * If HRTV validation contains some ineligible items, return PolicyValidatorErrors.INELIGIBLE_ITEMS
 * Otherwise, return PolicyValidatorErrors.GENERIC.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export var getErrorType = function (error) {
    var _a, _b;
    var message = error.message;
    var code = (_a = getAPIErrorData(error)) === null || _a === void 0 ? void 0 : _a.code;
    if (message === 'PARTIAL_INELIGIBLE_ITEMS') {
        return PolicyValidatorErrors.PARTIAL_INELIGIBLE_ITEMS;
    }
    if (message === 'ALL_INELIGIBLE_ITEMS') {
        return PolicyValidatorErrors.ALL_INELIGIBLE_ITEMS;
    }
    if (code === 'SSR_UPS_ERR_1002') {
        return PolicyValidatorErrors.SSR_UPS_ERR_1002;
    }
    var isNetworkError = ((_b = error === null || error === void 0 ? void 0 : error.response) === null || _b === void 0 ? void 0 : _b.status) === 500 ||
        (error === null || error === void 0 ? void 0 : error.code) === 'ERR_NETWORK' ||
        (error === null || error === void 0 ? void 0 : error.code) === 'ECONNABORTED';
    if (isNetworkError)
        return PolicyValidatorErrors.NETWORK;
    var isMultiShipmentError = isMultiShipmentAddressError(error);
    if (isMultiShipmentError)
        return PolicyValidatorErrors.MULTI_SHIPMENTS;
    return PolicyValidatorErrors.GENERIC;
};
export var fetchRefundEstimationAPI = function (refundRequest) {
    return RefundEstimationAPI(refundRequest)
        .then(function (response) {
        var refund = response.data.refund;
        if (refund) {
            return refund;
        }
        throw new Error('RefundEstimationAPI: Empty 2xx Response');
    })
        .catch(function (error) {
        TagApiError(error);
        return undefined;
    });
};
export var handleAddressValidateCall = function (request) {
    return ValidateAddressAPI(request).then(function (response) {
        return response.data;
    });
};
export var handleZipCodeValidateCall = function (request) {
    return ValidateZipCodeAPI(request).then(function (response) {
        return response.data;
    });
};
export var handleUPSReturnCreateCall = function (requestSsReturnCreate, setIsCreateReturnLoading, setError) {
    var temporaryRequest = __assign(__assign({}, requestSsReturnCreate), { order: __assign({}, requestSsReturnCreate) });
    setIsCreateReturnLoading(true);
    return SSReturnCreateAPI(temporaryRequest)
        .then(function (response) {
        var _a;
        var returnSuccessData = (_a = response === null || response === void 0 ? void 0 : response.data) === null || _a === void 0 ? void 0 : _a.returnService;
        if (!returnSuccessData ||
            returnSuccessData.status !== 'success' ||
            !returnSuccessData.ssrCOMReturnServiceResponse) {
            throw new Error('No return data found');
        }
        return returnSuccessData.ssrCOMReturnServiceResponse;
    })
        .catch(function (error) {
        TagFinalizeReturnApiError(error);
        setError(getAPIErrorData(error) || unknownError);
    })
        .finally(function () {
        setIsCreateReturnLoading(false);
    });
};
export var handleSORISFinalizeReturnCall = function (request, setIsCreateReturnLoading, setError) {
    setIsCreateReturnLoading(true);
    return FinalizeSorisReturnAPI(request)
        .then(function (response) {
        var returnSuccessData = response === null || response === void 0 ? void 0 : response.data;
        if (!returnSuccessData) {
            throw new Error('No return data found');
        }
        return returnSuccessData;
    })
        .catch(function (error) {
        TagFinalizeReturnApiError(error);
        setError(getAPIErrorData(error) || unknownError);
    })
        .finally(function () {
        setIsCreateReturnLoading(false);
    });
};
